import React from 'react';
import { ChangeEvent } from 'react';
import { Glossary, BoardGL } from 'common/utils/glossary';
import {
  Button,
  Message,
  Separator,
  Title,
  Spinner,
  Notes
} from 'common/components';
import { SubButtonType } from 'common/components/button';
import { DialogType, StateType } from 'common/modules/domain/deviceActivate';

interface DeviceActivateBoardProps {
  /** DeviceActivateBoardの表示有無 */
  isDisplay: boolean;
  /** DeviceActivateBoardから表示されるボードが表示されている時のDeviceActivateBoardの表示有無*/
  isDisplaySub: boolean;
  /** グロッサリ */
  glossary: Glossary;
  /** 言語 */
  language: string;
  /** デバイス利用上限 */
  planLimit: number;
  /** デバイス利用量 */
  planUsage: number;
  /** API実行中か否か */
  isProcessing: boolean;
  /** 取得が失敗か否か */
  isDeviceActivateListProcessingFail: boolean;
  /** 取得が成功したか否か */
  isDeviceActivateListProcessingUntreated: boolean;
  /** 有償機能予約取り消しAPI実行中か否か */
  isDeviceActivateReserveCancelProcessing: boolean;
  /**
   * 有償機能アクティベート情報マップ
   * {端末ID(機種ID+シリアル番号): 有償機能アクティベート情報}
   **/
  deviceActivateMap: {
    [key: string]: {
      /** モデル名 */
      modelName: string;
      /** 状態("active"：有効 or "reserve"：予約) */
      status: StateType;
      /** 機種ID */
      modelId: string;
      /** シリアル番号 */
      serialNo: string;
      /** アップロード日時 (ISO8601 UTC) */
      uploadAt: string;
      /** 削除チェックボックスの状態(true or false) */
      isDelete: boolean;
    };
  };
  /** ダイアログのモード(通常モード/選択削除モード) */
  dialogMode: DialogType;
  /** ファイル選択後に動作する処理 */
  onClickUpload: (e: ChangeEvent<HTMLInputElement>) => void;
  /** "アクティベートファイルのダウンロードボタン"の押下処理 */
  onClickDownload: () => void;
  /** "クローズボタン"の押下処理 */
  onClickClose: () => void;
  /** "削除ボタン"の押下処理 */
  onClickDelete: () => void;
  /** 削除対象フラグ更新処理 */
  updateDelete: (terminalId: string, isDelete: boolean) => void;
  /** "選択ボタン"の押下時の処理 */
  onClickSelect: () => void;
}

/**
 * 有償機能アクティベート画面のPresentation Component
 */
export const DeviceActivateBoard: React.FC<DeviceActivateBoardProps> = ({
  isDisplay,
  isDisplaySub,
  glossary,
  language,
  planLimit,
  planUsage,
  isProcessing,
  isDeviceActivateListProcessingFail,
  isDeviceActivateListProcessingUntreated,
  deviceActivateMap,
  dialogMode,
  onClickUpload,
  onClickDownload,
  onClickClose,
  onClickDelete,
  updateDelete,
  onClickSelect
}) => {
  /** チェックボックス押下時の処理 */
  const onClickCheckBox = (key: string) => {
    /** 対象データの削除対象フラグを更新 */
    updateDelete(key, !deviceActivateMap[key].isDelete);
  };

  const dispLoanTime = (createAt: string) => {
    if (createAt == '') {
      return createAt;
    }
    return new Intl.DateTimeFormat(language, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric'
    }).format(new Date(createAt));
  };

  // リスト部に表示するComponentをメモライズ
  const listComponent = React.useMemo(() => {
    return Object.entries(deviceActivateMap).map(([key, value]) =>
      window.innerWidth > 639 || document.documentElement.clientWidth > 639 ? (
        <div key={key}>
          <label
            onClick={
              dialogMode === DialogType.DELETE &&
              value.status === StateType.TYPE_RESERVE
                ? () => onClickCheckBox(key)
                : undefined
            }
            className={
              dialogMode === DialogType.NORMAL
                ? 'deviceActivateListLabel-not-cursor'
                : dialogMode === DialogType.DELETE &&
                  value.status === StateType.TYPE_ACTIVE
                ? 'deviceActivateListLabel-not-cursor-gray'
                : 'deviceActivateListLabel'
            }
          >
            <div className="deviceActivateListFlex">
              <div className="deviceActivateList-left">
                {dialogMode === DialogType.DELETE ? (
                  value.status === StateType.TYPE_RESERVE ? (
                    <Button
                      type="toolButton"
                      subType={value.isDelete ? 'checkOn' : 'checkOff'}
                      data-test="deviceActivateBoard-checkButton"
                      onClick={() => onClickCheckBox(key)}
                    />
                  ) : (
                    <span
                      className="checkDisabledIcon"
                      data-test="deviceActivateBoard-checkOff-icon"
                    />
                  )
                ) : (
                  <span className="deviceActivateBoard-not-checkIcon" />
                )}
                <div className="deviceActivateListBlock">
                  <div data-test="deviceActivateBoard-modelName-serialNo">
                    {value.modelName} / SN : {value.serialNo}
                  </div>
                  <div data-test="deviceActivateBoard-uploadAt">
                    {glossary.glx(BoardGL.UploadDate)}{' '}
                    {dispLoanTime(value.uploadAt)}
                  </div>
                </div>
              </div>
            </div>
          </label>
        </div>
      ) : (
        <div key={key}>
          <label
            onClick={
              dialogMode === DialogType.DELETE &&
              value.status === StateType.TYPE_RESERVE
                ? () => onClickCheckBox(key)
                : undefined
            }
            className={
              dialogMode === DialogType.NORMAL
                ? 'deviceActivateListLabel-not-cursor'
                : dialogMode === DialogType.DELETE &&
                  value.status === StateType.TYPE_ACTIVE
                ? 'deviceActivateListLabel-not-cursor-gray'
                : 'deviceActivateListLabel'
            }
          >
            <div className="deviceActivateListFlex">
              <div className="deviceActivateList-left">
                {dialogMode === DialogType.DELETE ? (
                  value.status === StateType.TYPE_RESERVE ? (
                    <Button
                      type="toolButton"
                      subType={value.isDelete ? 'checkOn' : 'checkOff'}
                      data-test="deviceActivateBoard-checkButton"
                      onClick={() => onClickCheckBox(key)}
                    />
                  ) : (
                    <span
                      className="checkDisabledIcon"
                      data-test="deviceActivateBoard-checkOff-icon"
                    />
                  )
                ) : (
                  <span className="deviceActivateBoard-not-checkIcon" />
                )}
                <div className="deviceActivateListBlock">
                  <div data-test="deviceActivateBoard-modelName-serialNo-value">
                    {value.modelName} / SN : {value.serialNo}
                  </div>
                  <div data-test="deviceActivateBoard-uploadDate">
                    {glossary.glx(BoardGL.UploadDate)}
                  </div>
                  <div data-test="deviceActivateBoard-uploadAt">
                    {dispLoanTime(value.uploadAt)}
                  </div>
                </div>
              </div>
            </div>
          </label>
        </div>
      )
    );
  }, [deviceActivateMap]);

  /** チェック状態によって削除ボタンのサブタイプを制御 */
  let subDeleteButtonType: SubButtonType = 'invalid';
  for (const value of Object.values(deviceActivateMap)) {
    if (value.isDelete == true) {
      // 一つでもチェックされていれば活性('highPriority')
      subDeleteButtonType = 'highPriority';
      break;
    }
  }

  /** ダウンロードボタンのサブタイプを制御 */
  let subDownloadButtonType: SubButtonType = 'highPriority';
  if (
    (isDeviceActivateListProcessingUntreated &&
      Object.keys(deviceActivateMap).length === 0) ||
    isDeviceActivateListProcessingFail ||
    isProcessing ||
    dialogMode === DialogType.DELETE
  ) {
    // アクティベート情報が0件の場合or取得に失敗した場合orAPI実行中の場合or選択削除モードの場合に非活性
    subDownloadButtonType = 'invalid';
  }

  /** アップロードボタンのサブタイプを制御 */
  let subUploadButtonType: SubButtonType = 'highPriority';
  if (isProcessing) {
    // API実行中の場合に非活性
    subUploadButtonType = 'invalid';
  }

  /** 選択ボタンのサブタイプを制御 */
  let subSelectButtonType: SubButtonType = 'highPriority';
  if (
    (isDeviceActivateListProcessingUntreated &&
      Object.keys(deviceActivateMap).length === 0) ||
    isDeviceActivateListProcessingFail ||
    isProcessing ||
    (isDeviceActivateListProcessingUntreated &&
      Object.values(deviceActivateMap).every(
        (value) => value.status === StateType.TYPE_ACTIVE
      ))
  ) {
    // アクティベート情報が0件の場合or取得に失敗した場合orAPI実行中の場合orリクエストファイルがすべてダウンロード済みの場合に非活性
    subSelectButtonType = 'invalid';
  }

  const _component = (
    <div className="deviceActivateBoard" data-test="deviceActivateBoard">
      <Button
        type="toolButton"
        subType="close"
        data-test="deviceActivateBoard-toolButton"
        onClick={onClickClose}
      />
      <Title type="title" data-test="deviceActivateBoard-title">
        {glossary.glx(BoardGL.ActivateCameraExtensions)}
      </Title>
      <Title
        type="listMessage"
        data-test="deviceActivateBoard-annotation-listMessage"
      >
        {glossary.glx(BoardGL.DeviceActivateBoardNote)}
      </Title>
      {window.innerWidth > 639 || document.documentElement.clientWidth > 639 ? (
        <div className="action-toolbar">
          <label className="uploadLabel" htmlFor="reqUpload">
            <input
              type="file"
              onChange={(e) => onClickUpload(e)}
              accept=".req.dat,.dat"
              style={{ display: 'none', width: '100%' }}
              id="reqUpload"
              disabled={false}
              multiple
              data-test="deviceActivateBoard-uploadButton"
            />
            <Button
              type="uploadButton"
              subType={subUploadButtonType}
              data-test="deviceActivateBoard-uploadButton"
              onClick={undefined}
            >
              {glossary.glx(BoardGL.ReqFileUpload)}
            </Button>
          </label>
          <div className="action-tool-select-delete">
            <Button
              type="selectButton"
              subType={subSelectButtonType}
              data-test="deviceActivateBoard-selectButton"
              onClick={onClickSelect}
            >
              {dialogMode === DialogType.NORMAL
                ? glossary.glx(BoardGL.Select)
                : glossary.glx(BoardGL.Canceled)}
            </Button>
            <Button
              type="deleteButton"
              subType={subDeleteButtonType}
              data-test="deviceActivateBoard-deleteButton"
              onClick={onClickDelete}
            >
              {glossary.glx(BoardGL.ReqFileDelete)}
            </Button>
          </div>
        </div>
      ) : (
        <div className="action-toolbar">
          <label className="uploadLabel" htmlFor="reqUpload">
            <input
              type="file"
              onChange={(e) => onClickUpload(e)}
              accept=".req.dat,.dat"
              style={{ display: 'none', width: '100%' }}
              id="reqUpload"
              disabled={false}
              multiple
              data-test="deviceActivateBoard-uploadButton"
            />
            <Button
              type="uploadButton"
              subType={subUploadButtonType}
              data-test="deviceActivateBoard-uploadButton"
              onClick={undefined}
            >
              {glossary.glx(BoardGL.ReqFileUpload)}
            </Button>
          </label>
          <div className="action-tool-select-delete">
            <Button
              type="selectButton"
              subType={subSelectButtonType}
              data-test="deviceActivateBoard-selectButton"
              onClick={onClickSelect}
            >
              {dialogMode === DialogType.NORMAL
                ? glossary.glx(BoardGL.Select)
                : glossary.glx(BoardGL.Canceled)}
            </Button>
            <Button
              type="deleteButton"
              subType={subDeleteButtonType}
              data-test="deviceActivateBoard-deleteButton"
              onClick={onClickDelete}
            >
              {glossary.glx(BoardGL.ReqFileDelete)}
            </Button>
          </div>
        </div>
      )}
      <Separator type="separator" data-test="deviceActivateBoardSeparator1" />
      <div className="action-tool-info">
        <span
          className="cameraIcon"
          data-test="deviceActivateBoard-camera-icon"
        />
        <p>:</p>
        <p data-test="deviceActivateBoard-usage">
          {isProcessing ? null : `( ${planUsage} / ${planLimit} )`}
        </p>
      </div>
      <div className="scrollArea">
        {isDeviceActivateListProcessingFail && (
          <Message
            type="message"
            className="errorMessage"
            data-test="deviceActivateBoard-message_error"
          >
            {glossary.glx(BoardGL.DeviceActivateBoardCouldNotGet)}
          </Message>
        )}

        {isDeviceActivateListProcessingUntreated &&
          Object.keys(deviceActivateMap).length === 0 &&
          !isProcessing && (
            <>
              <Notes
                notes={[glossary.glx(BoardGL.DeviceActivateBoardNotRegister)]}
                className="errorMessage"
                data-test="deviceActivateBoard-message_empty"
              />
              <Notes
                notes={[glossary.glx(BoardGL.PleaseReqFileUpload)]}
                className="errorMessage"
                data-test="deviceActivateBoard-message_empty_upload"
              />
            </>
          )}
        {!isProcessing && listComponent}
        {isProcessing ? <Spinner /> : null}
      </div>
      <div className="footer">
        <Title
          type="listMessage"
          data-test="deviceActivateBoard-download-listMessage"
        >
          {glossary.glx(BoardGL.ProceedToDownload)}
        </Title>
        <Button
          type="nextButton"
          subType={subDownloadButtonType}
          data-test="deviceActivateBoard-downloadButton"
          onClick={onClickDownload}
        >
          {glossary.glx(BoardGL.Next)}
        </Button>
      </div>
    </div>
  );
  return isDisplay || isDisplaySub ? _component : null;
};
